/**
 * Is a booking's copy of rent/deposit out of step with its room? (COL-360)
 *
 * The room is the source of truth for a tenant's BASE rent and deposit. The
 * booking carries a copy, and every derived view — the profile, the ledger,
 * reminders — reads the copy. When the two disagree, one of them is lying to
 * someone. This module names the disagreement so the admin surfaces can flag it
 * and offer the repair (admin_resync_booking_to_room) instead of leaving a
 * tenant on a room they are not being billed for.
 *
 * Only the decision lives here; callers own the data fetching, which is what
 * makes it testable.
 */

import type { ResyncBookingResult } from "@/hooks/v2/useReassignBookings";
import { formatEUR } from "@/lib/v2-format";

/** Cents difference below which two amounts count as the same figure. */
const EUR_EPSILON = 0.005;

export interface RoomBaseDriftInput {
  bookingRent: number | null | undefined;
  bookingDeposit: number | null | undefined;
  roomPrice: number | null | undefined;
  roomDeposit: number | null | undefined;
  /**
   * A settled deposit (stage >= key_pickup_pending) is history, not a copy of
   * the room — the same lock rule the tenant profile applies — so it is never
   * reported as drift.
   */
  depositLocked: boolean;
}

export interface RoomBaseDrift {
  rent: { booking: number; room: number } | null;
  deposit: { booking: number | null; room: number } | null;
}

function positive(n: unknown): number | null {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * The drift between a booking and its room, or null when they agree.
 *
 * - A booking with no rent of its own is not drifted: every reader already
 *   falls back to the room price, so there is no second truth to reconcile.
 * - A room with no base price cannot be a source of truth; nothing is reported.
 * - The expected deposit is the room's base deposit, else 2 x price —
 *   the same chain resolve_expected_deposit_eur uses.
 */
export function detectRoomBaseDrift(
  input: RoomBaseDriftInput
): RoomBaseDrift | null {
  const roomPrice = positive(input.roomPrice);
  if (roomPrice == null) return null;

  const bookingRent = positive(input.bookingRent);
  const rent =
    bookingRent != null && Math.abs(bookingRent - roomPrice) >= EUR_EPSILON
      ? { booking: bookingRent, room: roomPrice }
      : null;

  let deposit: RoomBaseDrift["deposit"] = null;
  if (!input.depositLocked) {
    const expected =
      positive(input.roomDeposit) ?? Math.round(roomPrice * 2 * 100) / 100;
    const bookingDeposit = positive(input.bookingDeposit);
    if (
      bookingDeposit != null &&
      Math.abs(bookingDeposit - expected) >= EUR_EPSILON
    ) {
      deposit = { booking: bookingDeposit, room: expected };
    }
  }

  if (!rent && !deposit) return null;
  return { rent, deposit };
}

/** What a sync to the room changed, for the toast after admin_resync_booking_to_room. */
export function describeResync(result: ResyncBookingResult): string {
  if (!result.synced) return "Nothing to sync — the room has no base price.";
  const bits: string[] = [];
  if (result.monthly_rent_before !== result.monthly_rent_after) {
    bits.push(
      `rent ${formatEUR(result.monthly_rent_before) ?? "—"} → ${formatEUR(result.monthly_rent_after) ?? "—"}`
    );
  }
  if (result.deposit_amount_before !== result.deposit_amount_after) {
    bits.push(
      `deposit ${formatEUR(result.deposit_amount_before) ?? "—"} → ${formatEUR(result.deposit_amount_after) ?? "—"}`
    );
  }
  if (result.rent_rows_repriced > 0) {
    bits.push(
      `${result.rent_rows_repriced} open invoice${result.rent_rows_repriced === 1 ? "" : "s"} re-priced`
    );
  }
  return bits.length ? bits.join(", ") + "." : "Already in step with the room.";
}
